// @givmo/app-charity — ACH entry form (CPA-WEB4).
//
// THE most sensitive input in the portal: where a verified nonprofit types its bank account + routing
// number to receive real grant money. §3.4 payment-data rails, enforced here:
//   - The full account number lives ONLY in this component's local `useState`, for the in-flight submit.
//   - On a successful POST we IMMEDIATELY reset the form to EMPTY_ACH_FORM — the entered number is
//     dropped from component state and never survives the submit.
//   - The number is NEVER lifted to a parent, NEVER put into query/cache state, NEVER echoed into a
//     toast / error / analytics / URL. The success handoff passes only the MASKED result profile up.
//   - After entry the number is unrecoverable from the UI: there is no read-back API and the profile
//     view shows only the last-4 mask.
//
// The submit calls POST /charity-admin/charities/{id}/payment-profile/ach via the raw `sdk` (mirrors the
// claim wizard). The 201 response carries the masked profile + an honest `message` (e.g. the name-
// mismatch-under-review notice) + the honest `flagged` flag; the parent surfaces those truthfully.

import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorMessage,
  Field,
  Icon,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { CheckCircle2, Landmark, Lock, ShieldCheck } from 'lucide-react';
import { sdk, isGivmoError, type CharityPaymentProfileResultOut } from '@givmo/api-client';
import { useMutation } from '@tanstack/react-query';
import {
  achSchema,
  dropNumberSeparators,
  EMPTY_ACH_FORM,
  type AchFormValues,
  ROUTING_NUMBER_MESSAGE,
  ROUTING_NUMBER_REGEX,
  ACCOUNT_NUMBER_MESSAGE,
  ACCOUNT_NUMBER_REGEX,
  ACCOUNT_NUMBER_CONFIRM_MESSAGE,
  ACCOUNT_HOLDER_MESSAGE,
  ROUTING_NUMBER_CHECKSUM_MESSAGE,
  hasValidAbaChecksum,
} from './payment-schemas';
import { useUnsavedChanges } from '../../lib/unsaved-changes';
import { useStepUpGate } from './use-step-up-gate';
import { isStepUpCancelled, unansweredRefusalLine } from './payment-step-up';

/** Copy for a change the admin declined to verify — the entered details are untouched and still valid. */
const STEP_UP_CANCELLED_MESSAGE =
  'Verification cancelled — your bank details weren’t saved. Submit again and verify it’s you to finish.';

const PREFIX = 'payment-ach-form';

export interface AchFormProps {
  charityId: string;
  /** Whether this is a CHANGE to existing bank details (vs. first-time entry) — drives the button copy. */
  isChange?: boolean;
  /**
   * A change whose details on file wait for a current Owner's entry (CPA-BE69): the Owner may be
   * entering the very account on file again, so the words say "your", never "new".
   */
  isReentry?: boolean;
  /** Called with the MASKED result (never any raw number) after a successful submit. */
  onSubmitted: (result: CharityPaymentProfileResultOut) => void | Promise<unknown>;
  /** Optional cancel affordance (e.g. to back out of a bank-change). */
  onCancel?: () => void;
}

export function AchForm({
  charityId,
  isChange = false,
  isReentry = false,
  onSubmitted,
  onCancel,
}: AchFormProps) {
  // The ONLY place the full account/routing numbers exist. Local, ephemeral, cleared on success.
  const [form, setForm] = useState<AchFormValues>(EMPTY_ACH_FORM);
  // `touched` gates inline errors so we don't shout before the user has typed in a field.
  const [touched, setTouched] = useState<Record<keyof AchFormValues, boolean>>({
    routing_number: false,
    account_number: false,
    confirm_account_number: false,
    account_holder_name: false,
    bank_name: false,
  });

  // AS-WEB3: `submit_ach` is step-up gated. The POST runs inside the gate as a thunk, so a stale-
  // identity refusal prompts for verification and re-sends THESE values — the numbers the admin
  // typed stay in `form` throughout and are never re-entered.
  const stepUp = useStepUpGate({ reentry: isReentry });

  const submit = useMutation({
    mutationFn: (values: AchFormValues): Promise<CharityPaymentProfileResultOut> =>
      stepUp.run(async () => {
        const { data } = await sdk.charityAdminSubmitCharityAch({
          path: { charity_id: charityId },
          body: {
            routing_number: values.routing_number,
            account_number: values.account_number,
            account_holder_name: values.account_holder_name,
            bank_name: values.bank_name?.trim() ? values.bank_name.trim() : null,
          },
          throwOnError: true,
        });
        return data;
      }),
    onSuccess: async (result) => {
      // §3.4: drop the entered numbers from state the instant the submit lands. From here on the UI has
      // only the masked profile. We DO NOT echo any number — not even into the toast/handoff.
      setForm(EMPTY_ACH_FORM);
      setTouched({
        routing_number: false,
        account_number: false,
        confirm_account_number: false,
        account_holder_name: false,
        bank_name: false,
      });
      await onSubmitted(result);
    },
    onError: (error: unknown) => {
      // A declined verification is not a bad submission: telling the admin to re-check a correct
      // account number is how a correct number gets edited into a wrong one.
      if (isStepUpCancelled(error)) {
        toast(STEP_UP_CANCELLED_MESSAGE);
        return;
      }
      // Never surface the entered numbers in an error. Only the server's vocabulary-clean message, and
      // a refusal nothing here could answer says its own cure (AS-PWL-2e), never "check them".
      toast.error(
        unansweredRefusalLine(error) ??
          (isGivmoError(error)
            ? error.message
            : 'We couldn’t save your bank details. Please check them and try again.'),
      );
    },
  });

  const parsed = achSchema.safeParse(form);
  const isValid = parsed.success;
  const isDirty = Object.values(form).some((value) => value.trim().length > 0);
  useUnsavedChanges(isDirty && !submit.isPending);

  // Per-field validity for inline messages (only shown once the field is touched).
  const routingError = touched.routing_number
    ? !ROUTING_NUMBER_REGEX.test(form.routing_number)
      ? ROUTING_NUMBER_MESSAGE
      : !hasValidAbaChecksum(form.routing_number)
        ? ROUTING_NUMBER_CHECKSUM_MESSAGE
        : undefined
    : undefined;
  const accountError =
    touched.account_number && !ACCOUNT_NUMBER_REGEX.test(form.account_number)
      ? ACCOUNT_NUMBER_MESSAGE
      : undefined;
  const confirmAccountError = touched.confirm_account_number
    ? !ACCOUNT_NUMBER_REGEX.test(form.confirm_account_number)
      ? ACCOUNT_NUMBER_MESSAGE
      : form.confirm_account_number !== form.account_number
        ? ACCOUNT_NUMBER_CONFIRM_MESSAGE
        : undefined
    : undefined;
  const holderError =
    touched.account_holder_name && form.account_holder_name.trim().length === 0
      ? ACCOUNT_HOLDER_MESSAGE
      : undefined;

  function update<K extends keyof AchFormValues>(key: K, value: AchFormValues[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <Card className="overflow-hidden" testId={`${PREFIX}-card`}>
      <CardHeader className="px-24 py-20">
        <div className="flex items-start gap-12">
          <span className="flex size-[40px] shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
            <Icon icon={Landmark} size={20} aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-s11 font-w600 uppercase tracking-wide text-brand-700">
              {isChange ? 'Update payment method' : 'Payment setup · Step 2 of 2'}
            </p>
            <h2 className="mt-2 text-s18 font-w700 text-text-strong">
              {isChange
                ? isReentry
                  ? 'Enter your bank details'
                  : 'Enter new bank details'
                : 'Add your bank account'}
            </h2>
            <p className="mt-4 text-s13 text-text-secondary">
              Use an account owned by your organization and able to receive ACH deposits.
            </p>
          </div>
        </div>
      </CardHeader>
      <CardBody className="px-24 py-24">
        <form
          className="flex flex-col gap-24"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (!isValid || submit.isPending) return;
            submit.mutate(parsed.data);
          }}
          {...testIdProp(`${PREFIX}-form`)}
        >
          <section aria-labelledby={`${PREFIX}-ownership-heading`}>
            <div className="mb-16 flex items-center justify-between gap-12">
              <div>
                <h3
                  id={`${PREFIX}-ownership-heading`}
                  className="text-s14 font-w600 text-text-strong"
                >
                  Account ownership
                </h3>
                <p className="mt-2 text-s12 text-text-tertiary">
                  This should match the legal owner of the bank account.
                </p>
              </div>
              <Badge tone="neutral" size="sm">
                Organization account
              </Badge>
            </div>
            <div className="grid grid-cols-1 gap-16 sm:grid-cols-2">
              <Field
                label="Account-holder name"
                helper="Enter the name exactly as it appears on the account."
                autoComplete="off"
                placeholder="Organization legal name"
                value={form.account_holder_name}
                onChange={(e) => update('account_holder_name', e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, account_holder_name: true }))}
                error={!!holderError}
                errorMessage={holderError}
                testId={`${PREFIX}-holder`}
              />
              <Field
                label="Bank name (optional)"
                helper="For your reference in the portal."
                autoComplete="off"
                placeholder="e.g. First National Bank"
                value={form.bank_name ?? ''}
                onChange={(e) => update('bank_name', e.target.value)}
                testId={`${PREFIX}-bank-name`}
              />
            </div>
          </section>

          <section
            className="border-t border-border-subtle pt-20"
            aria-labelledby={`${PREFIX}-numbers-heading`}
          >
            <div className="mb-16">
              <h3 id={`${PREFIX}-numbers-heading`} className="text-s14 font-w600 text-text-strong">
                Bank details
              </h3>
              <p className="mt-2 text-s12 text-text-tertiary">
                Find these numbers on a check or in your bank’s account details.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-16 sm:grid-cols-2">
              <Field
                label="Routing number"
                helper="The 9-digit ABA number for your bank."
                inputMode="numeric"
                autoComplete="off"
                placeholder="9 digits"
                // No `maxLength` on any bank-number field: the browser cuts a paste to fit BEFORE the
                // separators are dropped, so "021-000-021" would arrive as 7 digits. The schema holds
                // the 9 instead, with its message, and a letter stays in the field to be refused.
                value={form.routing_number}
                onChange={(e) => update('routing_number', dropNumberSeparators(e.target.value))}
                onBlur={() => setTouched((t) => ({ ...t, routing_number: true }))}
                error={!!routingError}
                errorMessage={routingError}
                testId={`${PREFIX}-routing`}
              />
              <Field
                label="Account number"
                helper="5–17 digits. Hidden while you type."
                inputMode="numeric"
                autoComplete="off"
                // The full number is entered here once, then dropped on submit. Password type keeps it from
                // shoulder-surfers and from being auto-saved/echoed; it is still never persisted by us.
                type="password"
                placeholder="5–17 digits"
                // No `maxLength` here either: "1234-5678-9012-3456" would arrive as 14 digits. The
                // schema holds the 17 instead, with its message.
                value={form.account_number}
                onChange={(e) => update('account_number', dropNumberSeparators(e.target.value))}
                onBlur={() => setTouched((t) => ({ ...t, account_number: true }))}
                error={!!accountError}
                errorMessage={accountError}
                testId={`${PREFIX}-account`}
              />
              <Field
                containerClassName="sm:col-start-2"
                label="Confirm account number"
                helper="Enter it again to prevent deposit errors."
                inputMode="numeric"
                autoComplete="off"
                type="password"
                placeholder="Re-enter account number"
                value={form.confirm_account_number}
                onChange={(e) =>
                  update('confirm_account_number', dropNumberSeparators(e.target.value))
                }
                onBlur={() => setTouched((t) => ({ ...t, confirm_account_number: true }))}
                error={!!confirmAccountError}
                errorMessage={confirmAccountError}
                testId={`${PREFIX}-account-confirm`}
              />
            </div>
          </section>

          <div
            className="flex items-start gap-10 rounded-lg border border-brand-100 bg-brand-50 p-14"
            {...testIdProp(`${PREFIX}-security-note`)}
          >
            <span className="flex size-28 shrink-0 items-center justify-center rounded-md bg-surface text-brand-600 shadow-xs">
              <Icon icon={Lock} size={15} aria-hidden />
            </span>
            <div>
              <p className="text-s13 font-w600 text-brand-800">Your details stay protected</p>
              <p className="mt-2 text-s12 leading-h13 text-text-secondary">
                Bank details are encrypted and used only to send your grants. After saving, only the
                last 4 digits are shown in this portal.
              </p>
            </div>
          </div>

          {isValid ? (
            <div
              className="rounded-xl border border-border bg-surface-sunken p-16"
              {...testIdProp(`${PREFIX}-review`)}
            >
              <div className="flex items-start gap-10">
                <Icon
                  icon={CheckCircle2}
                  size={18}
                  className="mt-1 shrink-0 text-success-600"
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <h3 className="text-s14 font-w600 text-text-strong">Review before saving</h3>
                  <p className="mt-2 text-s12 text-text-tertiary">
                    Confirm the masked details below are the account you intend to use.
                  </p>
                </div>
              </div>
              <dl className="mt-14 grid grid-cols-1 gap-12 border-t border-border-subtle pt-14 text-s13 sm:grid-cols-2">
                <div>
                  <dt className="text-s11 font-w600 uppercase tracking-wide text-text-tertiary">
                    Account holder
                  </dt>
                  <dd className="mt-2 truncate font-w500 text-text-strong">
                    {form.account_holder_name.trim()}
                  </dd>
                </div>
                <div>
                  <dt className="text-s11 font-w600 uppercase tracking-wide text-text-tertiary">
                    Bank
                  </dt>
                  <dd className="mt-2 truncate font-w500 text-text-strong">
                    {form.bank_name?.trim() || 'Not provided'}
                  </dd>
                </div>
                <div>
                  <dt className="text-s11 font-w600 uppercase tracking-wide text-text-tertiary">
                    Routing number
                  </dt>
                  <dd className="mt-2 font-w500 tabular-nums text-text-strong">
                    ••••• {form.routing_number.slice(-4)}
                  </dd>
                </div>
                <div>
                  <dt className="text-s11 font-w600 uppercase tracking-wide text-text-tertiary">
                    Account number
                  </dt>
                  <dd className="mt-2 font-w500 tabular-nums text-text-strong">
                    •••• {form.account_number.slice(-4)}
                  </dd>
                </div>
              </dl>
            </div>
          ) : null}

          {submit.isError ? (
            <ErrorMessage testId={`${PREFIX}-submit-error`}>
              {isStepUpCancelled(submit.error)
                ? STEP_UP_CANCELLED_MESSAGE
                : (unansweredRefusalLine(submit.error) ??
                  'We couldn’t save your bank details. Please check them and try again.')}
            </ErrorMessage>
          ) : null}

          <div className="flex flex-col-reverse items-stretch gap-8 border-t border-border-subtle pt-20 sm:flex-row sm:items-center sm:justify-end">
            {onCancel ? (
              <Button
                type="button"
                appearance="saas"
                tone="secondary"
                size="lg"
                onClick={onCancel}
                disabled={submit.isPending}
                testId={`${PREFIX}-cancel`}
              >
                Cancel
              </Button>
            ) : null}
            <Button
              type="submit"
              appearance="saas"
              tone="primary"
              size="lg"
              leadingIcon={<Icon icon={ShieldCheck} size={16} aria-hidden />}
              disabled={!isValid}
              loading={submit.isPending}
              testId={`${PREFIX}-submit`}
            >
              {isChange
                ? isReentry
                  ? 'Save bank details'
                  : 'Save new bank details'
                : 'Save bank account'}
            </Button>
          </div>
        </form>
      </CardBody>
      {/* Portalled — placement is for ownership, not layout. Rendered here so the prompt lives and
          dies with the form whose values its retry re-sends. */}
      {stepUp.dialog}
    </Card>
  );
}
